import { FC } from 'react'

import { useSearchParams } from '@remix-run/react'

import { BackButton, Container, PageHeading } from '~/components'
import { LinkList } from '~/features/common/link-list'
import { SearchForm } from '~/features/prefecture-page/components/search-form'
import { NewOffices, SearchByMap } from '~/features/top-page/components'
import { Area, Facility, Office, Prefecture, Type } from '~/models/types'

type PrefecturePageProps = {
  prefecture: Prefecture
  areas: Pick<Area, 'id' | 'name'>[]
  officeTypes: Type[]
  facilities: Pick<Facility, 'id' | 'name' | 'order'>[]
  newOffices: Office[]
  newOfficesCount: number
}

export const PrefecturePage: FC<PrefecturePageProps> = ({
  prefecture,
  areas,
  officeTypes,
  facilities,
  newOffices,
  newOfficesCount,
}) => {
  const areaFormOptions = areas.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }))

  const officeTypeFormOptions = officeTypes.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }))

  const facilitiesAndServicesFormOptions = facilities
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map(({ id, name }) => ({
      label: name,
      facilityId: id.toString(),
    }))

  const useMapLink = (prefectureSlug: string) => {
    const [searchParams] = useSearchParams()

    searchParams.set('prefectureSlug', prefectureSlug)

    const mapLink = `/map?${searchParams.toString()}`

    return {
      mapLink,
    }
  }

  const { mapLink } = useMapLink(prefecture.slug)

  return (
    <main className="flex w-full flex-col">
      <SearchByMap mapLink={mapLink} />
      <Container>
        <div className="mb-4 mt-20 flex justify-center md:mb-2">
          <PageHeading className="mb-4 mt-10 flex items-center gap-2">
            <img
              alt="Office icon"
              className="h-8 w-8"
              src="/icons/ic-office.svg"
            />
            {prefecture.name}のシェアオフィス・コワーキングスペースを探す
          </PageHeading>
        </div>
        <SearchForm
          areaFormOptions={areaFormOptions}
          className="mb-10"
          facilitiesAndServicesFormOptions={facilitiesAndServicesFormOptions}
          officeTypeFormOptions={officeTypeFormOptions}
        />
      </Container>
      <div className="flex justify-center bg-[#F8F8F8] py-12">
        <Container className="flex flex-col gap-x-8 gap-y-4 md:flex-row">
          <div className="order-2 w-full md:order-1 md:w-[248px]">
            <div className="order-2 w-full rounded-lg bg-white p-4 shadow-[0px_4px_40px_0px_rgba(0,0,0,0.08)] md:order-1 md:w-[248px]">
              <div className="mt-2.5 flex items-center rounded-md px-0 py-2">
                <img
                  alt="Office icon"
                  className="h-8 w-8"
                  src="/icons/ic-search.svg"
                />
                <h5 className="ml-2 text-xl font-bold">エリアから探す</h5>
              </div>
              <BackButton asChild className="my-4">
                <a href="/">都道府県を変更</a>
              </BackButton>
              <LinkList
                links={areas.map(({ id, name }) => ({
                  text: name,
                  href: `/prefectures/${prefecture.slug}/search?areas=${id}`,
                }))}
              />
            </div>
          </div>
          <div className="order-1 w-full md:order-2">
            <div className="rounded-lg bg-white p-4 shadow-[0px_4px_40px_0px_rgba(0,0,0,0.08)]">
              <NewOffices
                mapLink={mapLink}
                newOffices={newOffices}
                newOfficesCount={newOfficesCount}
                prefectureSlug={prefecture.slug}
              />
            </div>
          </div>
        </Container>
      </div>
    </main>
  )
}
